/*
  ============================================================================
  FILTER CONFIG — Club Partner custom product filters
  ============================================================================
  This file controls how EVERY filter on your collection pages looks and
  behaves: Brand, Club Type, Club Number, Flex, Shaft Material, Adaptor
  Brand, and all the rest. Editing this file does NOT change anything in
  Shopify itself (your metafield values and Vendor field stay exactly as
  they are) — it only changes how those values are labeled, grouped,
  ordered, and displayed in the filters shown on your website.

  You can safely edit this file yourself, or ask Claude to edit it for you,
  any time you want to fix a misspelling, change a grouping, rename a
  value, reorder a list, or change a filter's heading.

  Everything below lives in one place: window.CustomFilterFieldConfig.
  It's an entry per filter, keyed by the filter's internal name:

    brand, club_type, shaft_type, club_number, flex, handedness,
    shaft_tip_brand, condition, loft, shaft_material, lie_angle_color

  You don't have to fill in every section for every filter. Any filter left
  blank (or left out entirely) just shows its values exactly as they come
  from Shopify, sorted alphabetically, with a heading generated
  automatically from its name.

  Below is a walkthrough of everything you can control per filter. See the
  "brand" and "club_number" entries, since between them they use all of it.

  STEP 1 — "label": the heading shown above the filter on your site.
    label: "Club Type"

  STEP 2 — "fixes": correct typos/inconsistent spellings so they all count
  as the same value. Left side is the misspelled text exactly as it
  appears in Shopify, in lowercase. Right side is the corrected value.
    fixes: { "clevland": "Cleveland" }
    NOTE: You do NOT need to list simple capitalization differences like
    "Ping" vs "PIng" — matching already ignores case. Only list entries
    where the actual letters are different (typos, missing/extra spaces,
    punctuation, abbreviations).

  STEP 3 — "groups": merge one or more raw values into a single label a
  shopper sees and clicks on. The list next to each label is which raw
  value(s) count as that label.
    groups: { "Value Brands": ["PowerBilt", "Slazenger", "Orlimar"] }

    RENAMING A SINGLE VALUE: a "group" with only ONE value in its list is
    how you rename something. For example, to make the metafield value
    "Iron" display as "Individual Iron" everywhere on the site:
      groups: { "Individual Iron": ["Iron"] }
    That's the whole trick — one item on the right = a straight rename,
    more than one item = several raw values merged into one option.

  STEP 4 — "order": an optional list that puts specific values first, in
  the exact order you list them. Anything the shopper's products have that
  ISN'T in your list just gets sorted alphabetically (or numerically, see
  below) and tacked on after your list, so you never have to list every
  possible value — only the ones you care about ordering by hand.
    order: ["Driver", "Fairway Wood", "Hybrid"]
    Leave a filter's "order" out entirely and it keeps sorting
    alphabetically, exactly like before.

  OPTIONAL — "sort": set this to "numeric" on a filter whose values are
  numbers (like Loft) so "9.5" sorts before "10" instead of being sorted
  as text (where "10" would wrongly come before "9.5"). Only needed on
  filters made of numbers with no "order" list driving them.
    sort: "numeric"

  ADVANCED — "catchAllUnlisted" + "otherLabel". Brand has a LOT of messy
  vendor names, so it uses a catch-all: anything not listed anywhere in
  "groups" automatically gets bucketed into "otherLabel" (currently
  "Other"). Most filters leave catchAllUnlisted off, so any value you
  haven't mentioned just displays normally, unchanged.

  OPTIONAL — "startOpen": which filters are already expanded when a shopper
  first lands on the page. Every filter now starts COLLAPSED by default —
  the shopper sees a tidy list of filter headings and clicks one to open it.
  Add "startOpen: true" to any filter you want expanded right away.
  Currently only Club Type has it.
    startOpen: true
    Two things happen automatically and don't need configuring: a filter
    that already has something selected (someone opening a shared, filtered
    link) opens itself, and once a shopper opens or closes a filter by hand
    it stays that way while they keep filtering.

  OPTIONAL — "search": gives a filter its own little search box, inside the
  filter, just above its list of options. Brand has one, because 41 brands is
  a long list to scroll. Typing in it narrows the list as you type; it does
  NOT change which products are shown until a brand is actually ticked.
    search: true
    searchPlaceholder: "Search brands"   (the grey hint text; optional)

  Two extras come free with the search box:

    TYPING ANY SPELLING WORKS. Every raw value grouped into a brand finds it,
    so typing "vokey", "acushnet" or even the "titliest" typo all bring up
    Titleist, and "king cobra" brings up Cobra. Spaces, dots and dashes are
    ignored, so "labgolf" finds "L.A.B. Golf".

  OPTIONAL — "searchUnlisted": lets shoppers search the brands that are
  currently swept into "Other". Brand has this on. With it on, typing "hogan"
  brings up "Ben Hogan (8)" as its own tickable brand, even though Ben Hogan
  isn't in the "groups" list — tick it and they see only Ben Hogan clubs.
    searchUnlisted: true
    Those brands stay HIDDEN from the list until someone searches for them,
    so the Brand filter still shows your tidy curated list by default. They
    stay visible while they're ticked, and the "Other" option is unaffected.
    Names come through exactly as they're spelled in Shopify, except that an
    all-lowercase one gets capitalised ("ben hogan" shows as "Ben Hogan").
    Two spellings that differ only in capitals ("MacGregor" / "Macgregor")
    would show up as two separate brands, so pair those off in "fixes" below.
    Only filters with BOTH "search" and "searchUnlisted" behave this way;
    every other filter is untouched.

  IMPORTANT: whenever a filter has an "otherLabel" set — whether
  catchAllUnlisted is on (like Brand) or off (like Club Number, which
  manually groups a few odds-and-ends values into an "Other" group but
  leaves everything else alone) — that "Other" option always sorts to the
  very bottom of the list, no matter what "order" says. You never need to
  put "Other" at the end of your own "order" list; it's automatic.
  ============================================================================
*/
window.CustomFilterFieldConfig = {

  /* ---------------------------------------------------------------------
     CLUB TYPE
  --------------------------------------------------------------------- */
  club_type: {
    label: "Club Type",
    startOpen: false,  // set to true if you ever want this one expanded on page load
    groups: {
      "Driver": ["driver", "Driver"],
      "Hybrid": ["Hybrid", "Utility Club"],
      "Individual Iron": ["Iron"]
    },
    order: [
      "Driver", "Fairway Wood", "Hybrid", "Iron Set", "Wedge", "Putter",
      "Individual Iron", "Driving Iron", "Chipper", "Wood Set", "Complete Club Set"
    ]
  },

  /* ---------------------------------------------------------------------
     SHAFT TYPE
  --------------------------------------------------------------------- */
  shaft_type: {
    label: "Shaft Type",
    groups: {
      "Hybrid": ["Hybrid", "Utility Club"]
    },
    order: ["Driver", "Fairway Wood", "Hybrid"]
  },

  /* ---------------------------------------------------------------------
     CLUB NUMBER
     (uses a manual "Other" group for a couple of odds-and-ends values —
     catchAllUnlisted stays off, so everything else displays untouched,
     but "Other" itself still automatically sorts last. See the
     "otherLabel" note at the top of this file.)
  --------------------------------------------------------------------- */
  club_number: {
    label: "Club Number",
    catchAllUnlisted: false,
    otherLabel: "Other",
    groups: {
      "7 Wood": ["7-9 Wood", "7-Wood", "Heaven Wood"],
      "2 Hybrid": ["2-3 Hybrid", "2H"],
      "3 Hybrid": ["3H"],
      "4 Hybrid": ["4-5 Hybrid", "4H"],
      "5 Hybrid": ["5H"],
      "6 Hybrid": ["6H"],
      "Other": ["1-Wood", "99H"]
    },
    order: [
      "2-Wood", "3-Wood", "4-Wood", "5-Wood", "7 Wood", "9-Wood", "11-Wood",
      "Pitching Wedge", "Gap Wedge", "Sand Wedge", "Lob Wedge",
      "2 Hybrid", "3 Hybrid", "4 Hybrid", "5 Hybrid", "6 Hybrid", "7 Hybrid", "8 Hybrid",
      "1-Iron", "2-Iron", "3-Iron", "4-Iron", "5-Iron", "6-Iron", "7-Iron", "8-Iron", "9-Iron",
      "Other"
    ]
  },

  /* ---------------------------------------------------------------------
     FLEX
  --------------------------------------------------------------------- */
  flex: {
    label: "Flex",
    groups: {
      "Stiff": ["Firm", "Stiff", "Stiff Regular", "Stiff-Regular"],
      "Regular": ["Men", "Regular", "Uniflex", "Variable"],
      "Senior": ["Lite", "Seniors", "Senior"]
    },
    order: ["Extra Stiff", "Stiff", "Regular", "Senior", "Wedge", "Ladies", "Youth"]
  },

  /* ---------------------------------------------------------------------
     HANDEDNESS
  --------------------------------------------------------------------- */
  handedness: {
    label: "Handedness",
    order: ["Right-Handed", "Left-Handed"]
  },

  /* ---------------------------------------------------------------------
     BRAND
     No "fixes" here — every typo/spelling variant is folded straight into
     the group it belongs to below (e.g. "Cobra" catches "cobra" too,
     matching ignores case automatically, so you don't need to list both).
     Anything not listed in any group falls into "Other" (catchAllUnlisted
     is on for this filter — see the note at the top of this file).
  --------------------------------------------------------------------- */
  brand: {
    label: "Brand",
    catchAllUnlisted: true,
    otherLabel: "Other",

    /* The search box inside the Brand filter. "searchUnlisted" is what lets
       shoppers find and tick the brands that sit inside "Other" — see the
       walkthrough at the top of this file. */
    search: true,
    searchUnlisted: true,
    searchPlaceholder: "Search brands",

    /* FIXES — spellings that need correcting or merging.

       Most of these are the "Other" brands, which now show up by name in the
       Brand search box. Capitals inside a word can't be guessed, so they're
       spelled out here; the entries that pair up (Top Flite / Top-Flite)
       exist so the two spellings show as ONE brand instead of two.

       Left side = exactly what's in Shopify's Vendor field, in lowercase.
       Right side = how you want it to read. */
    fixes: {
      "hogan":               "Ben Hogan",
      "macgregor":           "MacGregor",
      "powerbilt":           "PowerBilt",
      "power bilt":          "PowerBilt",
      "top flite":           "Top Flite",
      "top-flite":           "Top Flite",
      "teardrop":            "TearDrop",
      "tear drop":           "TearDrop",
      "purespin":            "PureSpin",
      "pure spin":           "PureSpin",
      "turbo power":         "Turbo Power",
      "turbo-power":         "Turbo Power",
      "walter hagen":        "Walter Hagen",
      "walter hagan":        "Walter Hagen",
      "hagen":               "Walter Hagen",
      "xfactor":             "X Factor",
      "x factor":            "X Factor",
      "warrior":             "Warrior",
      "warrior custom golf": "Warrior",
      "warrior products":    "Warrior",
      "ram":                 "Ram",
      "vintage spalding":    "Spalding"
    },

    /* LOGOS — the small brand logo shown in the bottom-right corner of each
       product card on the collection pages.

       Left side  = the brand name exactly as it appears in the "groups" list
                    below (the name a shopper sees in the Brand filter).
       Right side = the image file name, which must exist in the theme's
                    assets folder, i.e. C:\Users\Josh\custom-filter\assets

       A brand that isn't listed here simply shows no logo — no gap, no
       broken image, nothing. To add one later: put the image in the assets
       folder, add a line here, and push both files.

       Keep image files small (roughly 160px wide is plenty) — they load on
       every product card. */
    logos: {
      "Callaway":       "brand-logo-callaway.png",
      "Cleveland":      "brand-logo-cleveland.png",
      "Cobra":          "brand-logo-cobra.png",
      "Honma":          "brand-logo-honma.png",
      "L.A.B. Golf":    "brand-logo-lab-golf.png",
      "Mizuno":         "brand-logo-mizuno.png",
      "Nike":           "brand-logo-nike.png",
      "Odyssey":        "brand-logo-odyssey.png",
      "Ping":           "brand-logo-ping.png",
      "PXG":            "brand-logo-pxg.png",
      "Scotty Cameron": "brand-logo-scotty-cameron.png",
      "Srixon":         "brand-logo-srixon.png",
      "Taylormade":     "brand-logo-taylormade.png",
      "Titleist":       "brand-logo-titleist.png",
      "Wilson Staff":   "brand-logo-wilson-staff.png",
      "XXIO":           "brand-logo-xxio.png"
    },

    groups: {
      "Accra": ["Accra", "ACCRA"],
      "Adams": ["Adams", "Adams Golf", "AdamsGolf"],
      "Aldila": ["Aldila"],
      "Aretera": ["Aretera"],
      "Bettinardi": ["Bettinardi"],
      "Callaway": ["Callaway"],
      "Cleveland": ["Cleveland", "clevland"],
      "Cobra": ["Cobra", "cobra", "King Cobra", "Lady Cobra"],
      "Evnroll": ["Evnroll"],
      "Fujikura": ["Fujikura"],
      "Grafalloy": ["Grafalloy"],
      "Graphite Design": ["Graphite Design"],
      "Honma": ["Honma"],
      "KBS": ["KBS"],
      "L.A.B. Golf": ["L.A.B. Golf", "LAB Golf"],
      "LA Golf": ["LA Golf"],
      "Matrix Ozik": ["Matrix Ozik"],
      "Mitsubishi": ["Mitsubishi", "Mitsubishi Golf", "Mitsubishi Rayon"],
      "Miura": ["Miura"],
      "Mizuno": ["Mizuno"],
      "Newton": ["Newton"],
      "Nike": ["Nike"],
      "Oban": ["Oban"],
      "Odyssey": ["Odyssey", "Callaway Odyssey", "Toulon"],
      "Ping": ["Ping", "PIng"],
      "Project X": ["Project X"],
      "PXG": ["PXG"],
      "Rife": ["Rife", "Guerin Rife"],
      "Scotty Cameron": ["Scotty Cameron", "Titleist Scotty Cameron"],
      "SeeMore": ["SeeMore", "Seemore", "See More"],
      "Sik": ["Sik"],
      "Srixon": ["Srixon"],
      "Taylormade": ["Taylormade", "TaylorMade"],
      "Titleist": ["Titleist", "titleist", "TItleist", "Titliest", "Acushnet", "Titleist Vokey", "Vokey", "Vintage Acushnet", "Vintage Titleist"],
      "Tour Edge": ["Tour Edge", "Exotics"],
      "U.S. Kids": ["U.S. Kids", "U.S. Kids Golf", "US Kids", "US Kids Golf"],
      "UST Mamiya": ["UST Mamiya"],
      "VA Composites": ["VA Composites"],
      "Veylix": ["Veylix"],
      "Wilson Staff": ["Wilson Staff", "Wilson", "WIlson"],
      "XXIO": ["XXIO"]
    }
  },

  /* ---------------------------------------------------------------------
     ADAPTOR BRAND (metafield name: shaft_tip_brand)
  --------------------------------------------------------------------- */
  shaft_tip_brand: {
    label: "Adaptor Brand",
    groups: {
      "Ping": ["Ping", "Ping G400", "Ping G440"],
      "No Tip": ["No Tip", "UNCUT"]
    },
    order: ["Callaway", "Cobra", "Mizuno", "Ping", "PXG", "Srixon", "Taylormade", "Titleist", "No Tip"]
  },

  /* ---------------------------------------------------------------------
     CONDITION
  --------------------------------------------------------------------- */
  condition: {
    label: "Condition",
    order: ["Used", "New"]
  },

  /* ---------------------------------------------------------------------
     LOFT
     (no grouping — just sorted as numbers so "9.5" comes before "10"
     instead of being sorted as text.)
  --------------------------------------------------------------------- */
  loft: {
    label: "Loft",
    sort: "numeric"
  },

  /* ---------------------------------------------------------------------
     SHAFT MATERIAL
     (uses a manual "Other" group — catchAllUnlisted stays off, so
     anything not Hickory/Titanium displays untouched, but "Other" still
     automatically sorts last.)
  --------------------------------------------------------------------- */
  shaft_material: {
    label: "Shaft Material",
    otherLabel: "Other",
    groups: {
      "Other": ["Hickory", "Titanium"]
    },
    order: ["Graphite", "Steel", "Other"]
  },

  /* ---------------------------------------------------------------------
     LIE ANGLE COLOR
  --------------------------------------------------------------------- */
  lie_angle_color: {
    label: "Lie Angle Color (Ping Only)",
    order: [
      "Black", "Blue", "Brown", "Gold", "Green", "Maroon",
      "Orange", "Purple", "Red", "Silver", "White", "Yellow"
    ]
  },

  /* ---------------------------------------------------------------------
     PRICE
     The price filter is a min/max box rather than a list of values, so
     "groups", "order" and the rest don't apply to it. Only "label" and
     "startOpen" do.
  --------------------------------------------------------------------- */
  price: {
    label: "Price",
    startOpen: false
  }
};
